// ═══════════════════════════════════════════════════════════
// Auth Store — V2 access + refresh token flow
// V1 compat: exposes token, isLoading, error, rights, hasRight, loadIncidents
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { login as apiLogin, setTokens, clearTokens } from '../api/client';
import { saveSession, loadSession, clearSession } from './session';
import type { V2User, AuthTokens } from '../api/types';

const INCIDENT_KEY = 'ogemc_activeIncident';

export interface Incident {
  id: number;
  name: string;
  type?: string;
  incidentType?: string; // alias for type (V1 compat)
  status?: string;
  location?: string;
  latitude?: string;
  longitude?: string;
  startDate?: string;
  endDate?: string;
  description?: string;
  commander?: string;
  severity?: string;
  createdByName?: string;
  createdByEmail?: string;
  datecreated?: string;
}

export interface TableRight {
  tableKey: string;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

interface AuthState {
  // Auth
  user: V2User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  sessionChecked: boolean;

  // V1 compat fields
  token: string | null;         // V1 pages use store.token for API auth headers
  isLoading: boolean;           // V1 login page shows spinner
  error: string | null;         // V1 login page shows error
  rights: TableRight[];         // V1 RBAC checks

  // Active incident context
  activeIncident: Incident | null;
  incidents: Incident[];

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  restoreSession: () => boolean;
  setActiveIncident: (incident: Incident | null) => void;
  setIncidents: (incidents: Incident[]) => void;
  loadIncidents: () => Promise<void>;
  hasRight: (tableKey: string, action: 'view' | 'update' | 'create' | 'delete') => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  tokens: null,
  isAuthenticated: false,
  sessionChecked: false,
  token: null,
  isLoading: false,
  error: null,
  rights: [],
  activeIncident: null,
  incidents: [],

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiLogin(email, password);
      const raw: any = res.user || {};
      const user: V2User = {
        id: raw.id || '',
        email: raw.email || email,
        fullName: raw.fullName || raw.name || email.split('@')[0],
        firstName: raw.firstName || (raw.name || '').split(' ')[0] || '',
        lastName: raw.lastName || (raw.name || '').split(' ').slice(1).join(' ') || '',
        role: raw.role || 'participant',
        isAdmin: raw.role === 'super_admin' || raw.role === 'admin' || raw.isAdmin || false,
      };
      const tokens: AuthTokens = {
        accessToken: res.accessToken,
        refreshToken: res.refreshToken || '',
        expiresAt: Date.now() + (res.expiresIn || 3600) * 1000,
      };
      setTokens(tokens);
      saveSession({ tokens, user });
      set({
        user, tokens,
        token: res.accessToken,  // V1 compat
        isAuthenticated: true,
        sessionChecked: true,
        isLoading: false,
      });
      // Load incidents and role after login
      await get().loadIncidents();
      try {
        const { useRoleStore } = await import('../store/roleStore');
        await useRoleStore.getState().loadMyRole(user.email);
      } catch (e) { console.warn('loadMyRole failed:', e); }
    } catch (err: any) {
      set({ isLoading: false, error: err?.message || 'Login failed' });
      throw err;
    }
  },

  logout: () => {
    clearTokens();
    clearSession();
    try { localStorage.removeItem(INCIDENT_KEY); } catch {}
    set({
      user: null, tokens: null, token: null,
      isAuthenticated: false,
      activeIncident: null, incidents: [],
    });
  },

  restoreSession: () => {
    const session = loadSession();
    if (session) {
      setTokens(session.tokens);
      set({
        user: session.user,
        tokens: session.tokens,
        token: session.tokens.accessToken,  // V1 compat
        isAuthenticated: true,
        sessionChecked: true,
      });
      // Restore active incident
      try {
        const stored = localStorage.getItem(INCIDENT_KEY);
        if (stored) set({ activeIncident: JSON.parse(stored) });
      } catch {}
      // Load role (async, non-blocking — dashboard will re-render when it resolves)
      const email = session.user.email;
      import('../store/roleStore').then(({ useRoleStore }) => {
        useRoleStore.getState().loadMyRole(email).catch(e => console.warn('loadMyRole failed:', e));
      });
      return true;
    } else {
      set({ sessionChecked: true });
      return false;
    }
  },

  setActiveIncident: (incident) => {
    set({ activeIncident: incident });
    try {
      if (incident) localStorage.setItem(INCIDENT_KEY, JSON.stringify(incident));
      else localStorage.removeItem(INCIDENT_KEY);
    } catch {}
  },

  setIncidents: (incidents) => {
    set({ incidents });
    // Auto-select first active incident if none selected
    const current = get().activeIncident;
    if (!current && incidents.length > 0) {
      const active = incidents.find(i => i.status === 'active') || incidents[0];
      get().setActiveIncident(active);
    }
  },

  loadIncidents: async () => {
    // Delegate to incident store which handles V2 API fetch + calls setIncidents
    try {
      const { useIncidentStore } = await import('../store/incident-store');
      await useIncidentStore.getState().fetch();
    } catch (e) {
      console.warn('loadIncidents failed:', e);
    }
  },

  hasRight: (_tableKey, _action) => {
    // V2 uses role-based RBAC, not per-table rights.
    // V1 pages use this for UI guards — default true so nothing is hidden unexpectedly.
    const { user } = get();
    return !!user;
  },
}));
